import React, { useState, useRef, KeyboardEvent, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button, useToast } from '@caos/ca-component-library';
import { submitSkill, getSubmissionStatus, SubmissionStatus } from '../lib/marketplace';
import ConfirmDialog from '../components/ConfirmDialog';
import CopyButton from '../components/CopyButton';
import usePageTitle from '../hooks/usePageTitle';
import './CreateSkillPage.css';

const STORAGE_KEY = 'create-skill-draft';

const TAG_SUGGESTIONS = [
  'productivity', 'git', 'writing', 'engineering',
  'sql', 'data', 'debugging', 'communication',
];

const BODY_PLACEHOLDER = `## Overview
Brief description of what this skill does.

## Behavior
Step-by-step instructions for Claude...

## Example Output
Show what good output looks like...`;

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;
const POLL_INTERVAL_MS = 5000;

function generateSkillMd(fields: {
  skillId: string;
  displayName: string;
  author: string;
  description: string;
  tags: string[];
  body: string;
}): string {
  const tagsArray = fields.tags.length > 0 ? fields.tags : ['general'];
  const tagsStr = JSON.stringify(tagsArray);
  return `---
name: ${fields.skillId || 'my-skill'}
description: "${fields.description.replace(/"/g, '\\"')}"
metadata:
  display_name: ${fields.displayName || 'My Skill'}
  author: ${fields.author || 'Author'}
  version: "1.0.0"
  tags: ${tagsStr}
---

${fields.body}`;
}

function loadDraft() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* corrupted — ignore */ }
  return null;
}

function saveDraft(draft: Record<string, unknown>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch { /* quota — ignore */ }
}

function clearDraft() {
  localStorage.removeItem(STORAGE_KEY);
}

export default function CreateSkillPage() {
  usePageTitle('Submit a Skill');
  const { showToast, dismissToast } = useToast();
  const draft = useRef(loadDraft()).current;

  const [displayName, setDisplayName] = useState(draft?.displayName ?? '');
  const [skillId, setSkillId] = useState(draft?.skillId ?? '');
  const [idManuallyEdited, setIdManuallyEdited] = useState(draft?.idManuallyEdited ?? false);
  const [author, setAuthor] = useState(draft?.author ?? '');
  const [description, setDescription] = useState(draft?.description ?? '');
  const [tags, setTags] = useState<string[]>(draft?.tags ?? []);
  const [tagInput, setTagInput] = useState('');
  const [body, setBody] = useState(draft?.body ?? '');

  const [showPreview, setShowPreview] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  useEffect(() => {
    saveDraft({ displayName, skillId, idManuallyEdited, author, description, tags, body });
  }, [displayName, skillId, idManuallyEdited, author, description, tags, body]);

  const [errors, setErrors] = useState<Partial<Record<'skillId' | 'displayName' | 'author' | 'description' | 'body', string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Polling state
  const [branch, setBranch] = useState<string | null>(null);
  const [pollStatus, setPollStatus] = useState<SubmissionStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const validatingToastRef = useRef<string | null>(null);

  const tagInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!idManuallyEdited) {
      setSkillId(slugify(displayName));
    }
  }, [displayName, idManuallyEdited]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Clean up interval on unmount
  useEffect(() => () => stopPolling(), [stopPolling]);

  const startPolling = useCallback((branchName: string) => {
    const check = async () => {
      try {
        const status = await getSubmissionStatus(branchName);
        setPollStatus(status);
        if (status === 'passed') {
          stopPolling();
          if (validatingToastRef.current) dismissToast(validatingToastRef.current);
          showToast({ message: 'Skill passed validation!', type: 'success', duration: 5000 });
        } else if (status === 'failed') {
          stopPolling();
          if (validatingToastRef.current) dismissToast(validatingToastRef.current);
          showToast({ message: 'Skill validation failed.', type: 'error', duration: 5000 });
        }
      } catch {
        // network hiccup — keep polling silently
      }
    };

    check();
    pollRef.current = setInterval(check, POLL_INTERVAL_MS);
  }, [stopPolling, showToast, dismissToast]);

  function handleClearForm() {
    setDisplayName('');
    setSkillId('');
    setIdManuallyEdited(false);
    setAuthor('');
    setDescription('');
    setTags([]);
    setTagInput('');
    setBody('');
    setErrors({});
    clearDraft();
    setShowClearConfirm(false);
    showToast({ message: 'Form cleared', type: 'info', duration: 2000 });
  }

  function addTag(raw: string) {
    const trimmed = raw.trim().toLowerCase();
    if (trimmed && !tags.includes(trimmed)) {
      setTags((prev) => [...prev, trimmed]);
    }
    setTagInput('');
  }

  function removeTag(tag: string) {
    setTags((prev) => prev.filter((t) => t !== tag));
  }

  function handleTagKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
    } else if (e.key === 'Backspace' && tagInput === '' && tags.length > 0) {
      setTags((prev) => prev.slice(0, -1));
    }
  }

  function validate(): boolean {
    const next: typeof errors = {};
    if (!displayName.trim()) next.displayName = 'Display name is required.';
    if (!skillId.trim()) {
      next.skillId = 'Skill ID is required.';
    } else if (!SLUG_RE.test(skillId)) {
      next.skillId = 'Only lowercase letters, numbers, and hyphens. Must start with a letter or number.';
    }
    if (!author.trim()) next.author = 'Author name is required.';
    if (!description.trim()) next.description = 'Trigger description is required.';
    if (!body.trim()) next.body = 'Skill body is required.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    setSubmitting(true);
    try {
      const branchName = await submitSkill({
        name: skillId,
        display_name: displayName,
        author,
        description,
        tags,
        body,
      });
      clearDraft();
      setBranch(branchName);
      validatingToastRef.current = showToast({ message: 'Skill submitted — validating...', type: 'info', loading: true, duration: 0 });
      startPolling(branchName);
    } catch (err: any) {
      setSubmitError(err?.message || 'Something went wrong. Please try again.');
      showToast({ message: 'Submission failed', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  // After submit — show polling UI instead of form
  if (branch) {
    return (
      <div className="create-skill-page">
        <nav className="create-skill-breadcrumb">
          <Link to="/" className="create-skill-back">&larr; Back to marketplace</Link>
        </nav>

        <div className="create-skill-status-card">
          {pollStatus === null || pollStatus === 'running' ? (
            <>
              <div className="create-skill-status-spinner" />
              <h2 className="create-skill-status-title">Validating your skill…</h2>
              <p className="create-skill-status-body">
                The pipeline is running tests on your submission. This usually takes under a minute.
                You can safely leave this page — validation will continue in the background.
              </p>
              <p className="create-skill-status-branch">{branch}</p>
            </>
          ) : pollStatus === 'passed' ? (
            <>
              <div className="create-skill-status-icon create-skill-status-icon--success">✓</div>
              <h2 className="create-skill-status-title">Skill submitted!</h2>
              <p className="create-skill-status-body">
                Your skill passed validation and will appear in the marketplace within a minute or two.
              </p>
              <Link to="/" className="create-skill-back">Back to marketplace</Link>
            </>
          ) : (
            <>
              <div className="create-skill-status-icon create-skill-status-icon--fail">✕</div>
              <h2 className="create-skill-status-title">Validation failed</h2>
              <p className="create-skill-status-body">
                Your skill didn't pass the automated tests. Check the skill body for formatting issues and try again.
              </p>
              <Button size="sm" onClick={() => { setBranch(null); setPollStatus(null); }}>
                Edit and resubmit
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="create-skill-page">
      <nav className="create-skill-breadcrumb">
        <Link to="/" className="create-skill-back">&larr; Back to marketplace</Link>
      </nav>

      <header className="create-skill-header">
        <div className="create-skill-header-row">
          <div>
            <h1 className="create-skill-title">Submit a Skill</h1>
            <p className="create-skill-subtitle">
              Contribute a new Claude Code skill to the CA marketplace.
            </p>
          </div>
          <button
            type="button"
            className={`create-skill-preview-toggle${showPreview ? ' create-skill-preview-toggle--active' : ''}`}
            onClick={() => setShowPreview((v) => !v)}
          >
            {showPreview ? 'Edit' : 'Preview SKILL.md'}
          </button>
        </div>
      </header>

      {submitError && (
        <div className="create-skill-error" role="alert">
          {submitError}
        </div>
      )}

      {showPreview ? (
        <div className="create-skill-preview">
          <div className="create-skill-preview-header">
            <span className="create-skill-preview-filename">SKILL.md</span>
            <CopyButton text={generateSkillMd({ skillId, displayName, author, description, tags, body })} label="Copy" />
          </div>
          <pre className="create-skill-preview-content">{generateSkillMd({ skillId, displayName, author, description, tags, body })}</pre>
        </div>
      ) : (
      <form className="create-skill-form" onSubmit={handleSubmit} noValidate>
        <div className="create-skill-row create-skill-row--two-col">
          <div className={`create-skill-field${errors.displayName ? ' create-skill-field--error' : ''}`}>
            <label className="create-skill-label" htmlFor="displayName">
              Display Name <span className="create-skill-required">*</span>
            </label>
            <input
              id="displayName"
              className="create-skill-input"
              type="text"
              placeholder="My Skill"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="off"
            />
            {errors.displayName && <span className="create-skill-field-error">{errors.displayName}</span>}
          </div>

          <div className={`create-skill-field${errors.skillId ? ' create-skill-field--error' : ''}`}>
            <label className="create-skill-label" htmlFor="skillId">
              Skill ID <span className="create-skill-required">*</span>
              <span className="create-skill-label-hint">auto-derived, editable</span>
            </label>
            <input
              id="skillId"
              className="create-skill-input create-skill-input--mono"
              type="text"
              placeholder="my-skill"
              value={skillId}
              onChange={(e) => {
                setIdManuallyEdited(true);
                setSkillId(e.target.value);
              }}
              autoComplete="off"
            />
            {errors.skillId && <span className="create-skill-field-error">{errors.skillId}</span>}
          </div>
        </div>

        <div className={`create-skill-field${errors.author ? ' create-skill-field--error' : ''}`}>
          <label className="create-skill-label" htmlFor="author">
            Your Name <span className="create-skill-required">*</span>
          </label>
          <input
            id="author"
            className="create-skill-input"
            type="text"
            placeholder="Jane Smith"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            autoComplete="name"
          />
          {errors.author && <span className="create-skill-field-error">{errors.author}</span>}
        </div>

        <div className={`create-skill-field${errors.description ? ' create-skill-field--error' : ''}`}>
          <label className="create-skill-label" htmlFor="description">
            Trigger Description <span className="create-skill-required">*</span>
            <span className="create-skill-label-hint">one sentence — when does Claude activate this skill?</span>
          </label>
          <textarea
            id="description"
            className="create-skill-textarea create-skill-textarea--short"
            rows={2}
            placeholder="Activate when the user asks to write a standup update."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          {errors.description && <span className="create-skill-field-error">{errors.description}</span>}
        </div>

        <div className="create-skill-field">
          <label className="create-skill-label" htmlFor="tagInput">
            Tags
            <span className="create-skill-label-hint">first tag becomes the category</span>
          </label>
          <div
            className="create-skill-tag-input-box"
            onClick={() => tagInputRef.current?.focus()}
          >
            {tags.map((tag) => (
              <span key={tag} className="create-skill-tag-pill">
                {tag}
                <button
                  type="button"
                  className="create-skill-tag-remove"
                  onClick={(e) => { e.stopPropagation(); removeTag(tag); }}
                  aria-label={`Remove ${tag}`}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              ref={tagInputRef}
              id="tagInput"
              className="create-skill-tag-text-input"
              type="text"
              placeholder={tags.length === 0 ? 'Type and press Enter or comma…' : ''}
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
              onBlur={() => { if (tagInput.trim()) addTag(tagInput); }}
              autoComplete="off"
            />
          </div>
          <div className="create-skill-tag-suggestions">
            {TAG_SUGGESTIONS.filter((s) => !tags.includes(s)).map((s) => (
              <button
                key={s}
                type="button"
                className="create-skill-tag-suggestion"
                onClick={() => addTag(s)}
              >
                + {s}
              </button>
            ))}
          </div>
        </div>

        <div className={`create-skill-field${errors.body ? ' create-skill-field--error' : ''}`}>
          <label className="create-skill-label" htmlFor="body">
            Skill Body <span className="create-skill-required">*</span>
            <span className="create-skill-label-hint">markdown — the full instructions Claude will follow</span>
          </label>
          <textarea
            id="body"
            className="create-skill-textarea create-skill-textarea--code"
            rows={12}
            placeholder={BODY_PLACEHOLDER}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          {errors.body && <span className="create-skill-field-error">{errors.body}</span>}
        </div>

        <div className="create-skill-actions">
          <Button
            type="submit"
            disabled={submitting}
          >
            {submitting ? (
              <span className="create-skill-spinner-row">
                <span className="create-skill-spinner" />
                Submitting…
              </span>
            ) : 'Submit Skill'}
          </Button>
          <button
            type="button"
            className="create-skill-clear"
            onClick={() => setShowClearConfirm(true)}
          >
            Clear form
          </button>
          <Link to="/" className="create-skill-cancel">Cancel</Link>
        </div>
      </form>
      )}

      <ConfirmDialog
        open={showClearConfirm}
        title="Clear form?"
        message="This will erase all fields and your saved draft. This cannot be undone."
        confirmLabel="Clear"
        cancelLabel="Keep editing"
        destructive
        onConfirm={handleClearForm}
        onCancel={() => setShowClearConfirm(false)}
      />
    </div>
  );
}
